<template>
  <div class="v-flex table-container">
    <div class="table-content-container">
      <b-table
        v-bind="$attrs"
        selectable
        select-mode="single"
        hover
        @sort-changed="e => $emit('sortChanged', e)"
        @row-clicked="handleClick"
      >
        <slot />
        <slot slot="thead-top" name="thead-top" />

        <template #head()="$attrs">
          <!-- A parent may take over one header cell with #head(<field key>), the same
               name b-table uses; anything else falls through to the icon / label. -->
          <slot :name="`head(${$attrs.field.key})`" v-bind="$attrs">
            <span v-if="$attrs.field.icon">
              <component
                :is="getIconComponent($attrs.field.icon.iconName)"
                :title="$attrs.field.icon.title"
                :size="22"
              />
            </span>
            <span v-else>
              {{ $attrs.label }}
            </span>
          </slot>
        </template>

        <template
          v-if="$attrs.busy && !($attrs.items && $attrs.items.length)"
          #table-busy
        >
          <div class="text-center my-2 table-loading">
            <b-spinner class="align-middle" />
          </div>
        </template>
        <template #head(checkbox)="$attrs">
          <b-checkbox
            v-if="$attrs.field.checkbox"
            :checked="isAllSelected"
            :indeterminate="isIndeterminate"
            class="custom-checkbox"
            @change="selectAll"
          />
        </template>

        <template #cell()="$attrs">
          <!-- The mirror of #head() above. Without it a parent's #cell(<field key>) is
               silently dropped: it lands in this wrapper's $scopedSlots and b-table never
               sees it. A named slot declared here would still win over this fallback. -->
          <slot :name="`cell(${$attrs.field.key})`" v-bind="$attrs">
            <div>
              <router-link
                v-if="$attrs.field.link === true"
                :to="$attrs.item.id"
                append
                :data-cy="makeSelector($attrs)"
              >
                {{ $attrs.item[$attrs.field.key] }}
              </router-link>

              <div
                v-else-if="$attrs.field.key === 'actions'"
                class="table-actions"
              >
                <span
                  v-for="action in filteredRowActions($attrs.item)"
                  :key="action.name"
                  v-b-tooltip.hover
                  :title="
                    action.isDisabled ? action.disabledTitle : action.iconTitle
                  "
                  :class="{ disabled: action.isDisabled }"
                  @click="!action.isDisabled && $emit(action.name, $attrs.item)"
                >
                  <component
                    :is="getIconComponent(action.icon)"
                    v-bind="action.props"
                    :size="20"
                  />
                </span>
              </div>

              <div v-else-if="$attrs.field.isStatus" class="status-cell">
                <CircleIcon
                  :class="getStatusClass($attrs.item[$attrs.field.key])"
                />
                {{ $attrs.item[$attrs.field.key] }}
              </div>
              <div v-else-if="$attrs.field.checkbox">
                <b-form-checkbox
                  v-if="checkboxIgnore($attrs)"
                  v-model="$attrs.item[$attrs.field.key]"
                  class="custom-checkbox"
                  @change="
                    $emit(
                      'checkboxChanged',
                      $attrs.item,
                      $attrs.item[$attrs.field.key]
                    )
                  "
                />
                <BlytzIconInfo
                  v-if="!checkboxIgnore($attrs)"
                  title="Auto generated templates must remain active"
                  class="info-icon-button-restyling"
                />
              </div>

              <div v-else-if="$attrs.field.template">template</div>

              <div v-else-if="hasFieldError($attrs)" class="error-cell">
                <span class="error-value">{{ formatOutput($attrs) }}</span>
              </div>

              <div v-else-if="$attrs.field.button">
                <b-button
                  v-model="$attrs.item[$attrs.field.key]"
                  class="custom-button"
                  @click="$emit('buttonClicked', $attrs.item)"
                >
                  {{ $attrs.field.button.text }}
                </b-button>
              </div>
              <div v-else-if="$attrs.field.tooltip">
                {{
                  $attrs.field.tooltip.display_parent
                    ? formatOutput($attrs)
                    : ''
                }}
                <span
                  v-if="$attrs.item[$attrs.field.tooltip.key]"
                  :id="`info-${$attrs.index}`"
                >
                  <b-button class="info-icon-button-restyling" disabled>
                    <Info icon="info" class="cursor-default" />
                  </b-button>
                </span>
                <b-tooltip
                  v-if="$attrs.item[$attrs.field.tooltip.key]"
                  :target="`info-${$attrs.index}`"
                  placement="left"
                >
                  <div>{{ $attrs.item[$attrs.field.tooltip.key] }}</div>
                </b-tooltip>
              </div>
              <div v-else-if="$attrs.field.info">
                {{ formatOutput($attrs) }}
                <span :id="`info-${$attrs.index}`">
                  <b-button class="info-icon-button-restyling" disabled>
                    <Info class="cursor-default" />
                  </b-button>
                </span>
                <b-tooltip :target="`info-${$attrs.index}`" placement="left">
                  <div
                    v-for="(item, index) in $attrs.field.info"
                    :key="item.label"
                  >
                    {{ item.label }}: {{ formatOutput($attrs, index) }}
                  </div>
                </b-tooltip>
              </div>

              <div v-else>
                <span
                  v-if="
                    $attrs.field.key === 'payment_origin' &&
                    $attrs.item.payment_origin_tooltip
                  "
                  :title="$attrs.item.payment_origin_tooltip"
                >
                  {{ formatOutput($attrs) }}
                </span>
                <!-- Follow-up text is red when overdue; the row itself never changes color for it. -->
                <span
                  v-else-if="$attrs.field.type === 'follow-up'"
                  :class="{
                    'text-danger': $attrs.item[$attrs.field.key]?.overdue
                  }"
                >
                  {{ formatOutput($attrs) }}
                </span>
                <template v-else>
                  {{ formatOutput($attrs) }}
                </template>
              </div>

              <small v-if="details($attrs) && !hasFieldError($attrs)">
                {{ details($attrs) }}
              </small>
            </div>
          </slot>
        </template>

        <template #row-details="row">
          <slot
            v-if="expandable && customExpandableContent"
            name="expandable"
            v-bind="row"
          />
          <small v-else-if="expandable && expandableMessage">
            {{ expandableContent() }}
          </small>
        </template>
      </b-table>
    </div>

    <TableFooter
      v-if="showFooter"
      :page-size-options="pageSizeOptions"
      :current-page-size="currentPageSize"
      :total-pages="totalPages"
      :current-page="currentPage"
      @pageChanged="handlePageChange"
      @pageSizeChanged="handlePageSizeChange"
    />
  </div>
</template>

<script>
import TableFooter from '@/components/Table/TableFooter.vue'
import {
  phoneFormatter,
  moneyFormatter,
  standardFormatter,
  dateTimeLocalFormatter,
  promiseFormatter,
  promiseDateFormatter,
  lastWorkedFormatter,
  basisPointsFormatter,
  accountStatusFormatter,
  followUpFormatter
} from '@/utils/formatters.ts'
import BlytzIconInfo from '@/components/Icons/BlytzIconInfo.vue'
import {
  Info,
  Circle as CircleIcon,
  Send,
  Edit,
  Pencil,
  Undo2,
  CircleX,
  Trash2,
  Eye,
  Settings,
  RefreshCw,
  Download,
  Upload,
  Plus,
  Minus,
  Key,
  MessageSquareDot
} from 'lucide-vue'

export default {
  components: {
    BlytzIconInfo,
    TableFooter,
    Info,
    CircleIcon,
    Send,
    Edit,
    Pencil,
    Undo2,
    CircleX,
    Trash2,
    Eye,
    Settings,
    RefreshCw,
    Download,
    Upload,
    Plus,
    Minus,
    Key
  },
  props: {
    pageSize: {
      default: 50,
      type: Number
    },
    currentPage: {
      default: 1,
      type: Number
    },
    totalPages: {
      default: null,
      type: Number
    },
    hasFooter: {
      default: true,
      type: Boolean
    },
    clickable: {
      default: () => {
        return true
      },
      type: [Function, Boolean]
    },
    customSelect: {
      default: false,
      type: Boolean
    },
    rowActions: {
      default: null,
      type: Array
    },
    expandable: {
      default: false,
      type: Boolean
    },
    customExpandableContent: {
      default: false,
      type: Boolean
    },
    expandableMessage: {
      default: () => '',
      type: [Function, String]
    },
    isAllSelected: {
      default: false,
      type: Boolean
    },
    isIndeterminate: {
      default: false,
      type: Boolean
    }
  },
  data() {
    return {
      pageSizeOptions: [50, 75, 100],
      currentPageSize: 50
    }
  },

  computed: {
    showFooter() {
      return this.hasFooter && this.$attrs?.items?.length >= 1
    }
  },

  mounted() {
    this.currentPageSize = this.pageSize
  },

  methods: {
    checkboxIgnore(attrs) {
      return !attrs?.field?.checkbox?.ignore?.includes(
        attrs.item[attrs?.field?.checkbox?.ignoreBy]
      )
    },

    details(attrs) {
      let result = ''
      if (attrs?.item?.details && attrs?.item?.details[attrs?.field?.key])
        result = attrs?.item?.details[attrs?.field?.key]

      return result
    },

    expandableContent() {
      return this.expandable
        ? this.$attrs.items.find(element => element._showDetails)?.payment[
            this.expandable
          ]
        : ''
    },

    hasFieldError(attrs) {
      return (
        attrs?.item?.details &&
        Object.prototype.hasOwnProperty.call(
          attrs.item.details,
          attrs.field.key
        )
      )
    },

    getFieldError(attrs) {
      return this.hasFieldError(attrs)
        ? attrs.item.details[attrs.field.key]
        : ''
    },

    getStatusClass(status) {
      const success = ['active']

      const warning = ['pending']

      const muted = ['closed', 'inactive']

      if (success.includes(status.toLowerCase())) return 'text-success'
      if (warning.includes(status.toLowerCase())) return 'text-warning'
      if (muted.includes(status.toLowerCase())) return 'text-muted'
    },

    handleClick(item) {
      if (this.expandable && this.clickable(item?.status)) {
        this.$attrs.items.map(ele => {
          if (ele.id === item.id && !ele._showDetails) {
            this.$set(ele, '_showDetails', true)
          } else {
            this.$set(ele, '_showDetails', false)
          }
        })
      } else if (!this.expandable && this.clickable) {
        if (this.customSelect) {
          this.$emit('handleClick', item)
        } else {
          const next = this.$route.path + '/' + item.id
          this.$router.push(next)
        }
      }
    },

    routeByName(attrs) {
      const routeTo = {
        name: attrs.field.link.name,
        params: { merchantID: this.$store.getters.profile.organization.id }
      }
      routeTo.params[attrs.field.link.paramID] = attrs.item.customerId

      return routeTo
    },

    formatOutput(attrs, index = 0) {
      let tableItem =
        attrs.item[
          attrs?.field?.info ? attrs?.field?.info[index].key : attrs.field.key
        ]

      if (attrs?.field?.parent) {
        const parent =
          attrs.item[attrs.field.parent][0] || attrs.item[attrs.field.parent]
        if (parent[attrs.field.key]) tableItem = parent[attrs.field.key]
      }

      switch (attrs?.type ?? attrs.field.type) {
        case 'phone':
          return phoneFormatter(tableItem)
        case 'amount':
          return moneyFormatter(tableItem)
        case 'datetime-local':
          return dateTimeLocalFormatter(tableItem)
        case 'promise':
          return promiseFormatter(tableItem)
        case 'promise-date': {
          const parent =
            attrs.item[attrs.field.parent][0] || attrs.item[attrs.field.parent]

          return promiseDateFormatter(
            parent?.promise_date,
            parent?.promise_status
          )
        }
        case 'last-worked':
          return lastWorkedFormatter(tableItem)
        case 'follow-up':
          return followUpFormatter(tableItem)
        case 'account-status':
          return accountStatusFormatter(tableItem)
        case 'basis-points':
          return basisPointsFormatter(tableItem)
        case 'info':
          return '-'
        default:
          return standardFormatter(tableItem)
      }
    },

    scrollToTop() {
      const container = this.$el.querySelector('.table-content-container')
      if (container) container.scrollTop = 0
    },

    handlePageChange(newPage) {
      this.$emit('pageChanged', newPage)
      this.scrollToTop()
    },

    handlePageSizeChange(newSize) {
      this.$emit('pageSizeChanged', newSize)
      this.scrollToTop()
    },

    makeSelector(attrs) {
      return attrs?.cy ?? attrs?.value?.toLowerCase() ?? ''
    },
    selectAll(event) {
      this.$emit('selectAll', event)
    },
    filteredRowActions(item) {
      if (!this.rowActions) return []

      return this.rowActions
        .filter(action => {
          // Only use visible function to filter out actions entirely
          if (typeof action.visible === 'function') {
            return action.visible(item)
          }

          return true
        })
        .map(action => {
          // Check if action should be disabled and add isDisabled flag
          const isDisabled =
            typeof action.disable === 'function' ? action.disable(item) : false
          const disabledTitle =
            typeof action.disabledTitle === 'function'
              ? action.disabledTitle(item)
              : action.disabledTitle

          return {
            ...action,
            isDisabled,
            disabledTitle: isDisabled ? disabledTitle : null
          }
        })
    },
    getIconComponent(iconName) {
      const iconMap = {
        Send,
        Edit,
        Pencil,
        Undo2,
        CircleX,
        Trash2,
        Eye,
        Settings,
        RefreshCw,
        Download,
        Upload,
        Plus,
        Minus,
        Info,
        CircleIcon,
        Key,
        MessageSquareDot
      }

      return iconMap[iconName] || iconName
    }
  }
}
</script>

<style lang="scss">
table > tbody > tr > :nth-child(1n) :not(.table-loading) {
  max-width: 100%;
  overflow: hidden;

  white-space: nowrap;
  text-overflow: ellipsis;

  // Every Bootstrap checkbox and switch draws its box outside its label.
  .custom-control label {
    overflow: visible;
  }
}

.custom-checkbox {
  .custom-control-input:checked,
  .custom-control-input:indeterminate {
    ~ .custom-control-label::before {
      background-color: $table-header-color !important;
      border: 1px solid $table-header-color;
      border-radius: 2px;
    }
  }

  .custom-control-label::after {
    padding-left: 7px;

    border: 1px solid $table-header-color;
    border-radius: 2px;
  }
}

.table-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;

  span {
    padding: 4px;

    border-radius: 4px;
    cursor: pointer;

    transition: background-color 0.2s ease;

    &:hover:not(.disabled) {
      background-color: rgba(255, 255, 255, 0.1);
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.4;
    }
  }
}
</style>

<style lang="scss" scoped>
.table-container {
  flex: 1;
  overflow: hidden;
}

.table-content-container {
  overflow: scroll;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none; /* Chrome */
  }
}

.custom-select {
  height: 25px;
  padding-top: 0;
  padding-bottom: 0;

  background-color: transparent;
}

.custom-control.custom-checkbox {
  padding-left: 2em;
}

.b-skeleton {
  width: 50px;
}

.action-cell-inner {
  display: flex;
  justify-content: flex-end;
}

.status-cell {
  display: flex;
  align-items: center;

  svg {
    height: 0.5rem;
  }
}
</style>
